"use client";

/**
 * ReadingProgress 컴포넌트
 * @description 글을 얼마나 읽었는지 헤더 위쪽 가장자리에 띠로 보인다. 브라우저 스크롤바는
 *   페이지 전체(푸터까지)의 위치라 글의 끝과 맞지 않는데, 이 띠는 **본문**의 끝에서 찬다.
 *   창에 고정되어 폭 전체를 쓴다 — 헤더 옆에 형제로 둔다(헤더 안에 두면 `backdrop-filter`
 *   가 기준 상자가 되어 열 폭에 갇힌다).
 * @example
 * <ReadingProgress targetId={POST_ARTICLE_ID} />
 * <Header inset="screen">…</Header>
 */
import { memo, useEffect, useRef } from "react";

import { readingProgressStyles } from "./ReadingProgress.styles";
import type { ReadingProgressProps } from "./ReadingProgress.types";
import { useReadingProgress } from "./useReadingProgress";

const COPY = { label: "Reading progress" } as const;

const ReadingProgress = memo<ReadingProgressProps>(
  ({ targetId, label = COPY.label, onComplete }) => {
    const { fillRef, percent } = useReadingProgress(targetId);
    const hasCompleted = useRef(false);

    useEffect(() => {
      if (percent < 100 || hasCompleted.current) return;
      hasCompleted.current = true;
      onComplete?.();
    }, [percent, onComplete]);

    return (
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className={readingProgressStyles.track}
      >
        <div
          ref={fillRef}
          aria-hidden
          className={readingProgressStyles.fill}
          style={{ transform: "scaleX(0)" }}
        />
      </div>
    );
  },
);

ReadingProgress.displayName = "ReadingProgress";

export default ReadingProgress;
